import { useState, useEffect, useRef, useCallback } from 'react';

export interface OrderBookEntry {
  price: number;
  amount: number;
  total: number;
}

export interface OrderBookData {
  bids: OrderBookEntry[];
  asks: OrderBookEntry[];
  lastUpdateId: number;
}

export interface Trade {
  id: number;
  price: number;
  amount: number;
  time: Date;
  isBuyerMaker: boolean;
}

export interface TickerData {
  symbol: string;
  price: number;
  priceChange: number;
  priceChangePercent: number;
  volume: number;
  quoteVolume: number;
  highPrice: number;
  lowPrice: number;
  timestamp: Date;
}

export const useBybitWebSocket = (symbols: string | string[]) => {
  const [orderBook, setOrderBook] = useState<OrderBookData>({
    bids: [],
    asks: [],
    lastUpdateId: 0
  });
  const [trades, setTrades] = useState<Trade[]>([]);
  const [tickers, setTickers] = useState<Record<string, TickerData>>({});
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // WebSocket reference
  const socketRef = useRef<WebSocket | null>(null);
  
  // Local order book state for processing updates
  const orderBookRef = useRef<{
    bids: Map<number, number>;
    asks: Map<number, number>;
  }>({
    bids: new Map(),
    asks: new Map()
  });

  // Reconnection state
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10; // Increased from 5 to 10 for more resilience
  const reconnectTimeoutRef = useRef<number | null>(null);
  const connectionTimeoutRef = useRef<number | null>(null);

  // Track subscribed symbols
  const subscribedSymbolsRef = useRef<Set<string>>(new Set());

  // Convert symbols to array if it's a string
  const symbolsArray = Array.isArray(symbols) ? symbols : [symbols];
  const primarySymbol = symbolsArray[0] || 'BTCUSDT';

  // Process order book snapshot
  const processOrderBookSnapshot = useCallback((data: any) => {
    if (!data || !data.b || !data.a) return;
    
    // Reset local order book state
    const bids = new Map<number, number>();
    const asks = new Map<number, number>();
    
    // Process bids
    data.b.forEach((bid: [string, string]) => {
      const price = parseFloat(bid[0]);
      const size = parseFloat(bid[1]);
      if (size > 0) {
        bids.set(price, size);
      }
    });
    
    // Process asks
    data.a.forEach((ask: [string, string]) => {
      const price = parseFloat(ask[0]);
      const size = parseFloat(ask[1]);
      if (size > 0) {
        asks.set(price, size);
      }
    });
    
    // Update local state
    orderBookRef.current = { bids, asks };
    
    // Convert Maps to sorted arrays with cumulative totals
    const sortedBids = Array.from(bids.entries())
      .sort((a, b) => b[0] - a[0]) // Sort by price descending (highest first)
      .map(([price, amount], index, array) => {
        // Calculate cumulative total
        const total = array
          .slice(0, index + 1)
          .reduce((sum, [_, qty]) => sum + qty, 0);
        
        return { price, amount, total };
      });
    
    const sortedAsks = Array.from(asks.entries())
      .sort((a, b) => a[0] - b[0]) // Sort by price ascending (lowest first)
      .map(([price, amount], index, array) => {
        // Calculate cumulative total
        const total = array
          .slice(0, index + 1)
          .reduce((sum, [_, qty]) => sum + qty, 0);
        
        return { price, amount, total };
      });
    
    // Update state with processed order book
    setOrderBook({
      bids: sortedBids,
      asks: sortedAsks,
      lastUpdateId: data.u || Date.now() // Use update ID or timestamp
    });
    
    setLastUpdated(new Date());
  }, []);

  // Process order book delta
  const processOrderBookDelta = useCallback((data: any) => {
    if (!data) return;
    
    const localOrderBook = orderBookRef.current;
    
    // Process bids
    if (data.b && Array.isArray(data.b)) {
      data.b.forEach((bid: [string, string]) => {
        const price = parseFloat(bid[0]);
        const size = parseFloat(bid[1]);
        
        if (size === 0) {
          localOrderBook.bids.delete(price);
        } else {
          localOrderBook.bids.set(price, size);
        }
      });
    }
    
    // Process asks
    if (data.a && Array.isArray(data.a)) {
      data.a.forEach((ask: [string, string]) => {
        const price = parseFloat(ask[0]);
        const size = parseFloat(ask[1]);
        
        if (size === 0) {
          localOrderBook.asks.delete(price);
        } else {
          localOrderBook.asks.set(price, size);
        }
      });
    }
    
    // Convert Maps to sorted arrays with cumulative totals
    const sortedBids = Array.from(localOrderBook.bids.entries())
      .sort((a, b) => b[0] - a[0]) // Sort by price descending (highest first)
      .map(([price, amount], index, array) => {
        // Calculate cumulative total
        const total = array
          .slice(0, index + 1)
          .reduce((sum, [_, qty]) => sum + qty, 0);
        
        return { price, amount, total };
      });
    
    const sortedAsks = Array.from(localOrderBook.asks.entries())
      .sort((a, b) => a[0] - b[0]) // Sort by price ascending (lowest first)
      .map(([price, amount], index, array) => {
        // Calculate cumulative total
        const total = array
          .slice(0, index + 1)
          .reduce((sum, [_, qty]) => sum + qty, 0);
        
        return { price, amount, total };
      });
    
    // Update state with processed order book
    setOrderBook({
      bids: sortedBids,
      asks: sortedAsks,
      lastUpdateId: data.u || Date.now() // Use update ID or timestamp
    });
    
    setLastUpdated(new Date());
  }, []);

  // Process trade message
  const processTrade = useCallback((data: any) => {
    if (!data || !Array.isArray(data)) return;
    
    const newTrades = data.map((trade: any) => ({
      id: parseInt(trade.i || trade.tradeId || Date.now().toString()),
      price: parseFloat(trade.p || trade.price),
      amount: parseFloat(trade.v || trade.size || trade.qty),
      time: new Date(trade.T || trade.tradeTime || trade.time || Date.now()),
      isBuyerMaker: trade.S === 'Sell' || trade.side === 'Sell' // In Bybit, 'Sell' means buyer was the maker
    }));
    
    // Add to beginning of trades array and limit to 100 trades
    setTrades(prevTrades => [...newTrades, ...prevTrades].slice(0, 100));
    setLastUpdated(new Date());
  }, []);

  // Process ticker message
  const processTicker = useCallback((data: any, symbol: string) => {
    if (!data) return;
    
    // Extract ticker data from different possible formats
    const price = parseFloat(data.lastPrice || data.last_price || '0');
    
    // Only create ticker data if price is valid and greater than 0
    if (isNaN(price) || price <= 0) {
      console.warn(`Invalid price for ${symbol}: ${price}`);
      return;
    }
    
    const tickerData: TickerData = {
      symbol: symbol,
      price: price,
      priceChange: parseFloat(data.price24hPcnt || data.price_24h_pcnt || '0') * price,
      priceChangePercent: parseFloat(data.price24hPcnt || data.price_24h_pcnt || '0') * 100,
      volume: parseFloat(data.volume24h || data.volume_24h || '0'),
      quoteVolume: parseFloat(data.turnover24h || data.turnover_24h || '0'),
      highPrice: parseFloat(data.highPrice24h || data.high_price_24h || '0'),
      lowPrice: parseFloat(data.lowPrice24h || data.low_price_24h || '0'),
      timestamp: new Date()
    };
    
    console.log(`WebSocket ticker update for ${symbol}: price=${tickerData.price}`);
    
    setTickers(prev => ({
      ...prev,
      [symbol]: tickerData
    }));
    
    setLastUpdated(new Date());
  }, []);

  // Subscribe to channels for a specific symbol
  const subscribeToSymbol = useCallback((symbol: string) => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.log(`WebSocket not ready, can't subscribe to ${symbol}`);
      return false;
    }

    if (subscribedSymbolsRef.current.has(symbol)) {
      console.log(`Already subscribed to ${symbol}`);
      return true; // Already subscribed
    }

    try {
      console.log(`Subscribing to ${symbol} channels...`);
      
      // Subscribe to orderbook for primary symbol only (to avoid overloading)
      if (symbol === primarySymbol) {
        socketRef.current.send(JSON.stringify({
          op: 'subscribe',
          args: [`orderbook.25.${symbol}`]
        }));
        
        // Subscribe to trades for primary symbol only
        socketRef.current.send(JSON.stringify({
          op: 'subscribe',
          args: [`publicTrade.${symbol}`]
        }));
      }
      
      // Subscribe to ticker for all symbols
      socketRef.current.send(JSON.stringify({
        op: 'subscribe',
        args: [`tickers.${symbol}`]
      }));
      
      // Add to subscribed symbols set
      subscribedSymbolsRef.current.add(symbol);
      console.log(`Successfully subscribed to ${symbol}`);
      return true;
    } catch (error) {
      console.error(`Error subscribing to ${symbol}:`, error);
      return false;
    }
  }, [primarySymbol]);

  // Connect to WebSocket with improved error handling
  const connectWebSocket = useCallback(() => {
    try {
      // Close existing connection
      if (socketRef.current) {
        socketRef.current.close();
      }
      
      // Clear existing timeouts
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      
      // Reset order book state
      orderBookRef.current = {
        bids: new Map(),
        asks: new Map()
      };
      
      // Reset subscribed symbols
      subscribedSymbolsRef.current.clear();
      
      // Connect to Bybit WebSocket
      const wsUrl = 'wss://stream.bybit.com/v5/public/linear';
      console.log(`Attempting to connect to Bybit WebSocket for ${symbolsArray.join(', ')}`);
      
      socketRef.current = new WebSocket(wsUrl);
      
      // Set connection timeout (increased from 30 to 45 seconds for more reliability)
      connectionTimeoutRef.current = setTimeout(() => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.CONNECTING) {
          console.warn(`WebSocket connection timeout for ${symbolsArray.join(', ')}`);
          socketRef.current.close();
          setError('Connection timeout');
        }
      }, 45000);
      
      socketRef.current.onopen = () => {
        console.log(`Connected to Bybit WebSocket for ${symbolsArray.join(', ')}`);
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
        
        // Clear connection timeout
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        
        // Subscribe to channels for each symbol
        if (socketRef.current) {
          symbolsArray.forEach(symbol => {
            subscribeToSymbol(symbol);
          });
        }
      };
      
      socketRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          // Handle different message types
          if (message.topic && message.topic.startsWith('orderbook')) {
            if (message.type === 'snapshot') {
              processOrderBookSnapshot(message.data);
            } else if (message.type === 'delta') {
              processOrderBookDelta(message.data);
            }
          } else if (message.topic && message.topic.startsWith('publicTrade')) {
            if (message.data) {
              processTrade(message.data);
            }
          } else if (message.topic && message.topic.startsWith('tickers.')) {
            if (message.data && message.data.length > 0) {
              // Extract symbol from topic (format: "tickers.BTCUSDT")
              const symbol = message.topic.split('.')[1];
              processTicker(message.data[0], symbol);
            }
          }
        } catch (err) {
          console.warn('Error processing WebSocket message:', err);
        }
      };
      
      socketRef.current.onerror = (event) => {
        console.warn(`WebSocket error for ${symbolsArray.join(', ')}:`, event);
        setError(`WebSocket connection error`);
        
        // Clear connection timeout
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
      };
      
      socketRef.current.onclose = (event) => {
        console.log(`WebSocket closed for ${symbolsArray.join(', ')}. Code: ${event.code}, Reason: ${event.reason}`);
        setIsConnected(false);
        
        // Clear connection timeout
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        
        // Only attempt to reconnect if it wasn't a manual close and we haven't exceeded max attempts
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
          const timeout = Math.min(Math.pow(2, reconnectAttemptsRef.current) * 1000, 30000); // Cap at 30 seconds
          console.log(`Attempting to reconnect in ${timeout}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
          
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, timeout);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.warn(`Failed to reconnect after ${maxReconnectAttempts} attempts - continuing with cached data`);
          setError(`WebSocket connection failed - using cached data`);
        }
      };
    } catch (err) {
      console.warn('Error connecting to WebSocket:', err);
      setError(`Failed to connect to WebSocket: ${err}`);
      setIsConnected(false);
    }
  }, [symbolsArray, primarySymbol, processOrderBookSnapshot, processOrderBookDelta, processTrade, processTicker, subscribeToSymbol]);

  // Connect to WebSocket on mount and when symbols change
  useEffect(() => {
    if (symbolsArray.length > 0) {
      connectWebSocket();
    }
    
    // Clean up on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
    };
  }, [symbolsArray, connectWebSocket]);

  // Subscribe to new symbols when they are added
  useEffect(() => {
    // Only attempt to subscribe if the WebSocket is connected
    if (isConnected && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      // Find symbols that are not yet subscribed
      const newSymbols = symbolsArray.filter(symbol => !subscribedSymbolsRef.current.has(symbol));
      
      if (newSymbols.length > 0) {
        console.log(`Subscribing to ${newSymbols.length} new symbols:`, newSymbols);
        
        // Subscribe to each new symbol
        newSymbols.forEach(symbol => {
          subscribeToSymbol(symbol);
        });
      }
    }
  }, [symbolsArray, isConnected, subscribeToSymbol]);

  // Implement a heartbeat/ping mechanism to keep the connection alive
  useEffect(() => {
    let pingInterval: number | null = null;
    
    if (isConnected && socketRef.current) {
      // Send a ping every 30 seconds to keep the connection alive
      pingInterval = setInterval(() => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
          try {
            // Send a ping message
            socketRef.current.send(JSON.stringify({ op: 'ping' }));
          } catch (err) {
            console.warn('Error sending ping:', err);
          }
        }
      }, 30000);
    }
    
    return () => {
      if (pingInterval) {
        clearInterval(pingInterval);
      }
    };
  }, [isConnected]);

  // Add a watchdog timer to detect stale connections
  useEffect(() => {
    let watchdogTimer: number | null = null;
    
    if (isConnected) {
      // Check if we've received updates in the last 60 seconds
      watchdogTimer = setInterval(() => {
        if (lastUpdated) {
          const now = new Date();
          const timeSinceLastUpdate = now.getTime() - lastUpdated.getTime();
          
          // If no updates for 60 seconds, reconnect
          if (timeSinceLastUpdate > 60000) {
            console.warn('No WebSocket updates received for 60 seconds, reconnecting...');
            
            // Force reconnection
            if (socketRef.current) {
              socketRef.current.close();
            }
          }
        }
      }, 30000);
    }
    
    return () => {
      if (watchdogTimer) {
        clearInterval(watchdogTimer);
      }
    };
  }, [isConnected, lastUpdated]);

  // Get ticker for primary symbol (for backward compatibility)
  const ticker = tickers[primarySymbol] || null;

  return {
    orderBook,
    trades,
    ticker,
    tickers,
    isConnected,
    error,
    lastUpdated
  };
};